import { Button } from "@mui/material";
import { FC } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { useAuthActions, useMicrosoftLogin } from "@ryde/redux";
import {
  getCompanyCodeSubDomain,
  Log,
  LoginAuthType,
  loginFlowStepAtom,
  LoginFormFlow,
  loginParamsAtom,
} from "@ryde/shared";

import { useLoginTranslation } from "../use-login-translation";
import { LoginOrSeparator } from "./login-or-separator";
import { buttonGreyBorderSx } from "./login.parts.styled";
import { useForceLoginParam } from "./use-force-login-param";

export const LoginAuthButtons: FC = () => {
  const { t } = useLoginTranslation();
  const loginParams = useRecoilValue(loginParamsAtom);
  const { handleMicrosoftLogin } = useMicrosoftLogin();
  const { handleSamlLogin, handleGoogleLogin } = useAuthActions();
  const companyCodeSubDomain = getCompanyCodeSubDomain();
  const setLoginFlowStep = useSetRecoilState(loginFlowStepAtom);
  const forceLogin = useForceLoginParam();

  const handleOauthLogin = (oauthType: LoginAuthType): void => {
    switch (oauthType) {
      case LoginAuthType.Microsoft:
        handleMicrosoftLogin({ forceLogin });
        return;
      case LoginAuthType.SSO:
        if (companyCodeSubDomain) {
          handleSamlLogin({ companyCode: companyCodeSubDomain, forceLogin });
        } else {
          setLoginFlowStep(LoginFormFlow.SAML_SSO_STEP);
        }

        return;
      case LoginAuthType.Google:
        handleGoogleLogin({ forceLogin });
        return;
      default:
        Log.infoAndToast(
          t("errors.thisTypeOfLoginWillBeImplementedSoon"),
          oauthType,
        );
        return;
    }
  };

  return (
    <>
      <div className="authButtons">
        {loginParams?.has(LoginAuthType.Google) && (
          <Button
            variant="outlined"
            sx={buttonGreyBorderSx}
            startIcon={<SVG_MUI.LogoGoogleSVG />}
            onClick={() => handleOauthLogin(LoginAuthType.Google)}
          >
            {t("login.Google")}
          </Button>
        )}
        {loginParams?.has(LoginAuthType.Microsoft) && (
          <Button
            variant="outlined"
            sx={buttonGreyBorderSx}
            startIcon={<SVG_MUI.LogoMicrosoftSVG />}
            onClick={() => handleOauthLogin(LoginAuthType.Microsoft)}
          >
            {t("login.Microsoft")}
          </Button>
        )}
        {loginParams?.has(LoginAuthType.SSO) && (
          <Button
            variant="outlined"
            sx={buttonGreyBorderSx}
            startIcon={<SVG_MUI.LogoMdiCloudSVG />}
            onClick={() => handleOauthLogin(LoginAuthType.SSO)}
          >
            {t("login.SSO")}
          </Button>
        )}
      </div>
      <LoginOrSeparator />
    </>
  );
};
